import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog } from '../ui/Dialog/Dialog';
import { Button } from '../atoms/Button/Button';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { useTranslation } from '../../i18n/useTranslation';
import { useVoiceConsentMutations, useVoiceSettings } from '../../hooks/useCaptures';
import './VoiceConsentDialog.css';

export interface VoiceConsentDialogProps {
  isOpen: boolean;
  /** Opened from a Record tap: "Allow & record" starts recording right after. */
  recordAfter?: boolean;
  onClose: () => void;
  /** Consent stored. */
  onGiven: () => void;
  onWriteInstead: () => void;
}

/**
 * First-use consent for voice notes (privacy review D3, wireframe "Voice
 * Consent"): what happens with the audio and the text, stored with its version
 * (legal_acknowledgements) and revocable in Settings.
 */
export function VoiceConsentDialog({ isOpen, recordAfter = false, onClose, onGiven, onWriteInstead }: VoiceConsentDialogProps) {
  const { t } = useTranslation();
  const detailsId = useId();
  const [showDetails, setShowDetails] = useState(false);
  const settings = useVoiceSettings(isOpen);
  const { give } = useVoiceConsentMutations();
  const version = settings.data?.consent.currentVersion;

  const allow = (): void => {
    if (!version) return;
    give.mutate(version, {
      onSuccess: () => {
        give.reset();
        onGiven();
      },
    });
  };

  const close = (): void => {
    give.reset();
    setShowDetails(false);
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      title={String(t('voice.consent.title'))}
      size="sm"
      data-testid="voice-consent-dialog"
      footer={
        <div className="voice-consent__actions">
          <Button variant="ghost" onClick={onWriteInstead}>
            {t('voice.recorder.writeInstead')}
          </Button>
          <Button variant="primary" onClick={allow} isLoading={give.isPending} disabled={!version}>
            {recordAfter ? t('voice.consent.allow') : t('voice.consent.allowOnly')}
          </Button>
        </div>
      }
    >
      <div className="voice-consent">
        <ul className="voice-consent__points">
          <li><Text>{t('voice.consent.pointAudio')}</Text></li>
          <li><Text>{t('voice.consent.pointText')}</Text></li>
          <li><Text>{t('voice.consent.pointReview')}</Text></li>
        </ul>
        <Text variant="caption" color="muted">{t('voice.consent.thirdParties')}</Text>
        <Text variant="caption" color="muted">{t('voice.consent.minors')}</Text>
        <div>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={showDetails}
            aria-controls={detailsId}
            onClick={() => setShowDetails((v) => !v)}
          >
            {t('voice.consent.details')}
          </Button>
          <div id={detailsId} hidden={!showDetails} className="voice-consent__details">
            <Text size="sm">{t('voice.consent.detailsBody')}</Text>
            <Text size="sm">
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                {t('voice.consent.privacyLink')}
              </Link>
            </Text>
          </div>
        </div>
        {give.isError && (
          <Alert
            compact
            title={t('voice.consent.errorTitle')}
            action={
              <Button variant="secondary" size="sm" onClick={allow}>
                {t('voice.consent.retry')}
              </Button>
            }
          >
            {t('voice.consent.errorBody')}
          </Alert>
        )}
      </div>
    </Dialog>
  );
}
